import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getPatientFolder,
  getFolderById,
  createFolder,
  updateFolderById,
  deleteFolderById,
  moveStudyToFolder,
  renameFolder,
  moveFolder,
  copyFolder,
  uploadFile,
  uploadFiles,
  deleteFile,
  updateFile,
  moveFile,
  copyFile,
  bulkFileOperation,
  toggleFileFavorite,
  downloadDicomFile,
  updateFileStats,
  getTotalStorage,
  getWorkspace,
  getFileDetails,
  getFolderDetails,
  getPatientStorage,
  getFilePreviewUrl,
  getFileDownloadUrl,
  getFolderZipEstimate,
  downloadFolderZip,
  attachFileToClaim,
  attachFileToClaimItem,
} from '@services/api/folder.api'
import {
  IFolder,
  IFile,
  ICreateFolderRequest,
  IUpdateFolderRequest,
  IMoveStudyRequest,
  IWorkspace,
  IFileDetails,
  IFolderDetails,
  IPreviewPayload,
  IDownloadPayload,
  IFolderZipEstimate,
} from '@dataTypes/folder.types'
import {
  getMockFileDetails,
  getMockFilePreviewUrl,
  getMockFolderDetails,
  getMockWorkspaceSnapshot,
  isMockFileId,
  isMockFolderId,
} from '@assets/mockdata/PatientTray/mockFilesData'
import {
  isPatientTrayMocksEnabled,
  shouldUsePatientTrayMockData,
} from '@utils/patientTrayMocks.util'

// Fetch patient folder (single folder for the patient)
export const fetchPatientFolder = createAsyncThunk<
  IFolder,
  string,
  { rejectValue: string }
>(
  'api/folder/fetch/patient/folder',
  async (patient_id, { rejectWithValue }) => {
    try {
      const response = await getPatientFolder(patient_id)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (response.data && 'folder' in response.data && response.data.folder) {
        return response.data.folder as IFolder
      }

      return rejectWithValue('Patient folder not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

// Fetch folder by ID
export const fetchFolderById = createAsyncThunk<
  IFolder,
  string,
  { rejectValue: string }
>('api/folder/fetch/id', async (id, { rejectWithValue }) => {
  try {
    const response = await getFolderById(id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data && 'folder' in response.data && response.data.folder) {
      return response.data.folder as IFolder
    }

    return rejectWithValue('Folder not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Create folder
export const createNewFolder = createAsyncThunk<
  IFolder,
  ICreateFolderRequest,
  { rejectValue: string }
>('api/folder/create', async (folderData, { rejectWithValue }) => {
  try {
    const response = await createFolder(folderData)
    if (response.statusCode !== 201 && response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data) {
      return response.data as IFolder
    }

    return rejectWithValue('Folder not created successfully')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Update folder
export const updateFolder = createAsyncThunk<
  IFolder,
  { id: string; updates: Partial<IUpdateFolderRequest> },
  { rejectValue: string }
>('api/folder/update', async ({ id, updates }, { rejectWithValue }) => {
  try {
    const response = await updateFolderById(id, updates)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data) {
      return response.data as IFolder
    }

    return rejectWithValue('Folder not updated successfully')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Delete folder
export const deleteFolder = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/folder/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteFolderById(id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Rename folder
export const renameFolderAction = createAsyncThunk<
  IFolder,
  { folderId: string; newName: string },
  { rejectValue: string }
>('api/folder/rename', async ({ folderId, newName }, { rejectWithValue }) => {
  try {
    const response = await renameFolder(folderId, newName)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data) {
      return response.data as IFolder
    }

    return rejectWithValue('Folder not renamed successfully')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const moveFolderAction = createAsyncThunk<
  IFolder,
  { folderId: string; targetParentFolderId: string | null },
  { rejectValue: string }
>(
  'api/folder/move',
  async ({ folderId, targetParentFolderId }, { rejectWithValue }) => {
    try {
      const response = await moveFolder(folderId, targetParentFolderId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return response.data as IFolder
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const copyFolderAction = createAsyncThunk<
  IFolder,
  { folderId: string; newName?: string },
  { rejectValue: string }
>('api/folder/copy', async ({ folderId, newName }, { rejectWithValue }) => {
  try {
    const response = await copyFolder(folderId, newName)
    if (response.statusCode !== 201 && response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data as IFolder
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Move study to folder
export const moveStudyToFolderAction = createAsyncThunk<
  any,
  IMoveStudyRequest,
  { rejectValue: string }
>('api/folder/move-study', async (moveData, { rejectWithValue }) => {
  try {
    const response = await moveStudyToFolder(moveData)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch total storage
export const fetchTotalStorage = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>('api/folder/fetch-total-storage', async (_, { rejectWithValue }) => {
  try {
    const response = await getTotalStorage()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (response.data && 'totalSize' in response.data) {
      return response.data.totalSize as number
    }

    return rejectWithValue('Total size not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch patient storage
export const fetchPatientStorage = createAsyncThunk<
  number,
  string,
  { rejectValue: string }
>(
  'api/folder/fetch-patient-storage',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await getPatientStorage(patientId)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return response.data.totalSize || 0
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

// Fetch patient workspace
export const fetchWorkspace = createAsyncThunk<
  IWorkspace,
  {
    patient_id: string
    folder_id?: string | null
    search?: string
    types?: string
    favorite?: 'true' | 'false'
    sort_by?: 'name' | 'date' | 'type'
    sort_dir?: 'asc' | 'desc'
    page?: number
    limit?: number
  },
  { rejectValue: string }
>('api/folder/workspace', async (params, { rejectWithValue }) => {
  if (shouldUsePatientTrayMockData(params.patient_id)) {
    const mock = getMockWorkspaceSnapshot(params.patient_id, params.folder_id)
    return mock ?? rejectWithValue('Mock folder not found')
  }
  try {
    const response = await getWorkspace({
      ...params,
      folder_id: params.folder_id ?? undefined,
    })
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch workspace payload without relying on reducer-managed workspace state
export const fetchWorkspaceSnapshot = createAsyncThunk<
  IWorkspace,
  {
    patient_id: string
    folder_id?: string | null
    search?: string
    types?: string
    favorite?: 'true' | 'false'
    sort_by?: 'name' | 'date' | 'type'
    sort_dir?: 'asc' | 'desc'
    page?: number
    limit?: number
  },
  { rejectValue: string }
>('api/folder/workspace-snapshot', async (params, { rejectWithValue }) => {
  try {
    const response = await getWorkspace({
      ...params,
      folder_id: params.folder_id ?? undefined,
    })
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch file details
export const fetchFileDetails = createAsyncThunk<
  IFileDetails,
  string,
  { rejectValue: string }
>('api/folder/file-details', async (fileId, { rejectWithValue }) => {
  if (isMockFileId(fileId) && isPatientTrayMocksEnabled()) {
    return getMockFileDetails(fileId) ?? rejectWithValue('Mock file not found')
  }
  try {
    const response = await getFileDetails(fileId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch folder details
export const fetchFolderDetails = createAsyncThunk<
  IFolderDetails,
  string,
  { rejectValue: string }
>('api/folder/folder-details', async (folderId, { rejectWithValue }) => {
  if (isMockFolderId(folderId) && isPatientTrayMocksEnabled()) {
    return (
      getMockFolderDetails(folderId) ?? rejectWithValue('Mock folder not found')
    )
  }
  try {
    const response = await getFolderDetails(folderId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch file preview payload
export const fetchFilePreview = createAsyncThunk<
  IPreviewPayload,
  string,
  { rejectValue: string }
>('api/folder/file-preview', async (fileId, { rejectWithValue }) => {
  if (isMockFileId(fileId) && isPatientTrayMocksEnabled()) {
    const url = getMockFilePreviewUrl(fileId)
    return url
      ? { is_dicom: false, url }
      : rejectWithValue('No preview for mock file')
  }
  try {
    const response = await getFilePreviewUrl(fileId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch file download payload
export const fetchFileDownload = createAsyncThunk<
  IDownloadPayload,
  string,
  { rejectValue: string }
>('api/folder/file-download', async (fileId, { rejectWithValue }) => {
  if (isMockFileId(fileId) && isPatientTrayMocksEnabled()) {
    const url = getMockFilePreviewUrl(fileId)
    return url ? { url } : rejectWithValue('No download for mock file')
  }
  try {
    const response = await getFileDownloadUrl(fileId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Fetch folder zip estimate
export const fetchFolderZipEstimate = createAsyncThunk<
  IFolderZipEstimate,
  string,
  { rejectValue: string }
>('api/folder/folder-zip-estimate', async (folderId, { rejectWithValue }) => {
  try {
    const response = await getFolderZipEstimate(folderId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Trigger server generated folder ZIP download
export const triggerFolderZipDownload = createAsyncThunk<
  { folderId: string; blob: Blob },
  string,
  { rejectValue: string }
>('api/folder/folder-zip-download', async (folderId, { rejectWithValue }) => {
  try {
    const blob = await downloadFolderZip(folderId)
    return { folderId, blob }
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Upload file
export const uploadFileAction = createAsyncThunk<
  IFile,
  {
    file: File
    patientId: string
    folderId?: string
    onUploadProgress?: (progressEvent: {
      loaded: number
      total?: number
    }) => void
  },
  { rejectValue: string }
>(
  'api/folder/upload-file',
  async (
    { file, patientId, folderId, onUploadProgress },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await uploadFile(
        file,
        patientId,
        folderId,
        onUploadProgress,
      )
      if (response.statusCode !== 201 && response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (response.data) {
        dispatch(fetchTotalStorage())
        return response.data as IFile
      }

      return rejectWithValue('File not uploaded successfully')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const uploadFilesAction = createAsyncThunk<
  IFile[],
  { files: File[]; patientId: string; folderId?: string },
  { rejectValue: string }
>(
  'api/folder/upload-files',
  async ({ files, patientId, folderId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await uploadFiles(files, patientId, folderId)
      if (response.statusCode !== 201 && response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      dispatch(fetchTotalStorage())
      return response.data.files || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

// Delete file
export const deleteFileAction = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/folder/delete-file', async (fileId, { rejectWithValue, dispatch }) => {
  try {
    const response = await deleteFile(fileId)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    dispatch(fetchTotalStorage())
    return fileId
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Toggle file favorite
export const toggleFileFavoriteAction = createAsyncThunk<
  IFile,
  { fileId: string; isFavorite: boolean },
  { rejectValue: string }
>(
  'api/folder/toggle-file-favorite',
  async ({ fileId, isFavorite }, { rejectWithValue }) => {
    try {
      const response = await toggleFileFavorite(fileId, isFavorite)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (response.data) {
        return response.data as IFile
      }

      return rejectWithValue('File favorite status not updated successfully')
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const renameFileAction = createAsyncThunk<
  IFile,
  { fileId: string; fileName: string },
  { rejectValue: string }
>(
  'api/folder/rename-file',
  async ({ fileId, fileName }, { rejectWithValue }) => {
    try {
      const response = await updateFile(fileId, { file_name: fileName })
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return response.data as IFile
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const moveFileAction = createAsyncThunk<
  IFile,
  { fileId: string; folderId: string | null },
  { rejectValue: string }
>('api/folder/move-file', async ({ fileId, folderId }, { rejectWithValue }) => {
  try {
    const response = await moveFile({
      file_id: fileId,
      folder_id: folderId || undefined,
    })
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data as IFile
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const bulkMoveFilesAction = createAsyncThunk<
  { processed_count: number; failed_count: number },
  { patientId: string; fileIds: string[]; folderId: string | null },
  { rejectValue: string }
>(
  'api/folder/bulk-move-files',
  async ({ patientId, fileIds, folderId }, { rejectWithValue }) => {
    try {
      const response = await bulkFileOperation({
        patient_id: patientId,
        file_ids: fileIds,
        operation: 'move',
        ...(folderId ? { folder_id: folderId } : {}),
      })
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      const { processed_count, failed_count, failures } = response.data
      if (failed_count > 0) {
        const joined =
          failures
            ?.map((f) => f.message)
            .filter(Boolean)
            .join(' ') ?? ''
        const detailMessage =
          failures?.length === 1
            ? failures[0].message
            : joined || `${failed_count} file(s) could not be moved.`
        return rejectWithValue(detailMessage)
      }
      return {
        processed_count,
        failed_count,
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const copyFileAction = createAsyncThunk<
  IFile,
  { fileId: string },
  { rejectValue: string }
>('api/folder/copy-file', async ({ fileId }, { rejectWithValue }) => {
  try {
    const response = await copyFile(fileId)
    if (response.statusCode !== 201 && response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return response.data as IFile
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const moveFilesAndFoldersAction = createAsyncThunk<
  { movedFiles: number; movedFolders: number },
  {
    patientId: string
    fileIds: string[]
    folderIds: string[]
    targetFolderId: string | null
  },
  { rejectValue: string }
>(
  'api/folder/move-files-and-folders',
  async (
    { patientId, fileIds, folderIds, targetFolderId },
    { rejectWithValue, dispatch },
  ) => {
    try {
      let movedFiles = 0
      if (fileIds.length > 0) {
        const bulkResult = await dispatch(
          bulkMoveFilesAction({
            patientId,
            fileIds,
            folderId: targetFolderId,
          }),
        )
        if (bulkMoveFilesAction.rejected.match(bulkResult)) {
          return rejectWithValue(
            (bulkResult.payload as string) || 'Could not move files.',
          )
        }
        movedFiles = bulkResult.payload.processed_count
      }

      let movedFolders = 0
      for (const folderId of folderIds) {
        const moveResult = await dispatch(
          moveFolderAction({
            folderId,
            targetParentFolderId: targetFolderId,
          }),
        )
        if (moveFolderAction.rejected.match(moveResult)) {
          return rejectWithValue(
            (moveResult.payload as string) || 'Could not move folder.',
          )
        }
        movedFolders += 1
      }

      return { movedFiles, movedFolders }
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

// Download file
export const downloadFileAction = createAsyncThunk<
  void,
  IFile,
  { rejectValue: string }
>('api/folder/download-file', async (file, { rejectWithValue }) => {
  try {
    // Check if this is a DICOM file and has the required DICOM UIDs
    if (file.is_dicom && file.study_uid && file.series_uid && file.sop_uid) {
      // Use DICOM Web module to fetch the file
      await downloadDicomFile(file)
      await updateFileStats(file.file_id)
    } else {
      // Fall back to direct file path download for non-DICOM files
    }
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

// Attach file to claim
export const attachFileToClaimAction = createAsyncThunk<
  unknown,
  { fileId: string; claimId: string; type?: string },
  { rejectValue: string }
>(
  'api/folder/attach-file-to-claim',
  async ({ fileId, claimId, type }, { rejectWithValue }) => {
    try {
      const response = await attachFileToClaim(fileId, claimId, type || '')
      if (response.statusCode !== 201 && response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to attach file to claim',
        )
      }
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

// Attach file to claim item
export const attachFileToClaimItemAction = createAsyncThunk<
  unknown,
  { fileId: string; claimItemId: string; type?: string },
  { rejectValue: string }
>(
  'api/folder/attach-file-to-claim-item',
  async ({ fileId, claimItemId, type }, { rejectWithValue }) => {
    try {
      const response = await attachFileToClaimItem(
        fileId,
        claimItemId,
        type || '',
      )
      if (response.statusCode !== 201 && response.statusCode !== 200) {
        return rejectWithValue(
          response.message || 'Failed to attach file to claim item',
        )
      }
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)
